import { useSelector } from 'react-redux';
import { Button, Link } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { InformationBanner } from 'src/components/molecules';
import { useIsFeatureEnabled } from 'src/hooks';
import { useTheme } from 'styled-components';
import * as Types from 'src/types';
import { FeatureFlag, FormState, State } from 'src/types';
import { trackCaseImportReviewStartedEvent } from 'src/utils/analytics/track/caseImport';
import { UploadScreen } from '../UploadCases';
import {
  ChecklistDescription,
  ChecklistSectionContainer,
  ChecklistSectionDetails,
  ChecklistSectionTitle,
  CtaContainer,
  PreImportChecklistContainer,
  PreImportInfoContainer,
  StyledChecklistContainer,
  StyledText,
  Title,
} from './PreImportChecklist.styles';

interface PreImportChecklistProps {
  setShowUploadModal: (show: boolean) => void;
  hasActiveJob: boolean;
  activeJobPercentage: number;
  stateProperties: {
    organisationId: string | null;
    userId: number | null;
    userSystemRole: string | null;
  };
  setScreen: (screen: UploadScreen) => void;
}

export const PreImportChecklist = ({
  setShowUploadModal,
  hasActiveJob,
  activeJobPercentage,
  stateProperties,
  setScreen,
}: PreImportChecklistProps) => {
  const theme = useTheme();
  const isRoutingEnabled = useIsFeatureEnabled(Types.FeatureFlag.ROUTING);
  const features = useSelector((state: State) => state.features);
  const trackingEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const isDynamicQuestionsEnabled = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );

  const preImportContent = [
    {
      title: 'Select the file with cases that you want to upload',
      description: (
        <div style={{ paddingLeft: '12px' }}>
          <li>File format should be .csv, .xls, .xlsx</li>
          <li>The maximum amount of cases in one file is 1000.</li>
        </div>
      ),
    },
    {
      title: 'Pre-define Categories',
      description:
        'Make sure that all the appropiate categories are set up in your system.',
      linkName: 'Go to Category configuration',
      link: '/categories',
    },
    {
      title: 'Import users beforehand and ensure the right roles are assigned',
      description:
        'Make sure that the user list is up-to-date in order to be able to assign the appropiate users to the appropiate cases.',
      linkName: 'Go to Manage Users',
      link: '/users',
    },
    ...(isRoutingEnabled
      ? [
          {
            title:
              'Pre-define routing rules to Auto assign cases without Case lead',
            description:
              'Make sure that routing rules are appropiately set up so that uploaded cases can be routed to the appropiate users.',
            linkName: 'Go to Routing',
            link: '/routing',
          },
        ]
      : []),
  ];

  return (
    <PreImportChecklistContainer data-testid="pre-import-checklist">
      <Title data-testid="pre-import-checklist-title">
        Pre-Import Checklist
      </Title>
      <PreImportInfoContainer>
        <StyledChecklistContainer>
          {preImportContent.map((item, index) => (
            <ChecklistSectionContainer
              key={index}
              data-testid={`checklist-item-${index}`}
            >
              <ChecklistSectionDetails>
                <ChecklistSectionTitle>
                  <StyledIcon
                    className={'uil uil-check-circle'}
                    size="24px"
                    color={
                      theme.isVaultVariant
                        ? theme.newPalette.neutral.N800
                        : theme.atlas.semantic.color.type.default
                    }
                  />
                  {item.title}
                </ChecklistSectionTitle>

                <ChecklistDescription>
                  {item.description}
                  {item.linkName && (
                    <Link
                      to={item.link}
                      size="medium"
                      data-testid={`checklist-link-${index}`}
                    >
                      {item.linkName}
                    </Link>
                  )}
                </ChecklistDescription>
              </ChecklistSectionDetails>
            </ChecklistSectionContainer>
          ))}
        </StyledChecklistContainer>
        <InformationBanner
          $fullWidth
          state={FormState.WARNING}
          title="Non-reversible process"
          description="Once uploaded, cases cannot be automatically removed. If you need to delete uploaded cases, you will have to do it manually."
          data-testid="warning-banner"
        />
        <StyledText>
          Please ensure that your file is import-ready by referring to the{' '}
          <Link
            data-testid="faq-link"
            size="medium"
            target="_blank"
            href="https://help.vaultplatform.com/en/articles/12811541-importing-cases-faq"
          >
            Vault FAQ{' '}
          </Link>
          section before uploading it.
        </StyledText>
      </PreImportInfoContainer>
      <CtaContainer>
        <Button
          onClick={() => {
            if (isDynamicQuestionsEnabled) {
              setScreen(UploadScreen.CATEGORY_SELECTION);
            } else {
              setShowUploadModal(true);
              if (trackingEnabled && stateProperties.organisationId) {
                trackCaseImportReviewStartedEvent({
                  organisation_id: Number(stateProperties.organisationId),
                  user_id: String(stateProperties.userId),
                  user_system_role: stateProperties.userSystemRole,
                });
              }
            }
          }}
          data-testid="continue-button"
          disabled={hasActiveJob}
        >
          <StyledIconInButton
            className={'uil uil-upload'}
            size="medium"
            position="left"
          />
          Continue to Upload & Map Data
        </Button>
        {hasActiveJob && (
          <StyledText data-testid="active-job-warning">
            An import is already in progress ({activeJobPercentage}%). You can
            start a new import once it’s finished.
          </StyledText>
        )}
      </CtaContainer>
    </PreImportChecklistContainer>
  );
};
